import type {
  FitnessPlanProgram,
  ProgramGenerationStep,
} from './fitness-plan.domain';
import {
  exerciseAllocationsHaveDisplay,
  generatedWorkoutsHaveDisplay,
  goalProfileHasDisplay,
  programPhaseHasDisplay,
  renderableWorkoutBlueprintsHaveDisplay,
  trainingStrategyHasDisplay,
  weeklyScheduleSplitHasDisplay,
} from './program-build-artifact-display.helpers';

const LEGACY_STEP_MAP: Record<string, ProgramGenerationStep> = {
  weekly_split: 'weekly_schedule_split',
  exercise_allocation: 'movement_slot_plan',
  validation: 'schema_validation',
};

export function normalizeGenerationStep(
  step: ProgramGenerationStep | string | undefined,
): ProgramGenerationStep | undefined {
  if (!step) {
    return undefined;
  }
  return LEGACY_STEP_MAP[step] ?? (step as ProgramGenerationStep);
}

export const BUILD_STEP_ORDER: ProgramGenerationStep[] = [
  'goal_profile',
  'training_strategy',
  'program_phase',
  'workout_blueprints',
  'movement_slot_plan',
  'exercise_selection',
  'weekly_schedule_split',
  'final_program_review',
  'schema_validation',
  'finalising',
];

export const BUILD_STEP_LABELS: Record<ProgramGenerationStep, string> = {
  goal_profile: 'Reading your goal',
  training_strategy: 'Building training strategy',
  program_phase: 'Creating program phase',
  workout_blueprints: 'Creating workout blueprints',
  weekly_schedule_split: 'Scheduling weekly split',
  movement_slot_plan: 'Planning movement slots',
  exercise_selection: 'Selecting exercises',
  final_program_review: 'Reviewing program',
  schema_validation: 'Checking program structure',
  finalising: 'Finalising program',
  weekly_split: 'Scheduling weekly split',
  exercise_allocation: 'Planning movement slots',
  validation: 'Checking program structure',
};

export type BuildStepUiState = 'pending' | 'active' | 'complete' | 'failed';

export type ArtifactUnlockMode = 'afterProduced' | 'duringOrAfter';

export function buildStepIndex(step: ProgramGenerationStep): number {
  const normalized = normalizeGenerationStep(step) ?? step;
  const idx = BUILD_STEP_ORDER.indexOf(normalized);
  return idx >= 0 ? idx : BUILD_STEP_ORDER.length;
}

export function stepAtLeast(
  current: ProgramGenerationStep | undefined,
  threshold: ProgramGenerationStep,
): boolean {
  if (!current) {
    return false;
  }
  return buildStepIndex(current) >= buildStepIndex(threshold);
}

export function buildActiveStatusMessage(program: FitnessPlanProgram): string {
  const msg = program.generation?.message?.trim();
  if (msg) {
    return msg;
  }
  const step = normalizeGenerationStep(program.generation?.currentStep);
  if (step) {
    return BUILD_STEP_LABELS[step];
  }
  return 'Generating…';
}

export function isProgramBuildRunning(program: FitnessPlanProgram): boolean {
  if (isProgramBuildFailed(program) || isProgramBuildComplete(program)) {
    return false;
  }
  const gen = program.generation?.status;
  if (gen === 'queued' || gen === 'running') {
    return true;
  }
  return program.generationStatus === 'processing';
}

export function isProgramBuildComplete(program: FitnessPlanProgram): boolean {
  return (
    program.generation?.status === 'completed' ||
    program.generationStatus === 'ready'
  );
}

export function isProgramBuildFailed(program: FitnessPlanProgram): boolean {
  return (
    program.generation?.status === 'failed' ||
    program.generationStatus === 'error'
  );
}

export function buildStepReached(
  program: FitnessPlanProgram,
  step: ProgramGenerationStep,
): boolean {
  const normalized = normalizeGenerationStep(step) ?? step;
  switch (normalized) {
    case 'goal_profile':
      return goalProfileHasDisplay(program.goalProfile);
    case 'training_strategy':
      return trainingStrategyHasDisplay(program.trainingStrategy);
    case 'program_phase':
      return programPhaseHasDisplay(program.phase);
    case 'workout_blueprints':
      return renderableWorkoutBlueprintsHaveDisplay(program.workoutBlueprints);
    case 'weekly_schedule_split':
      return weeklyScheduleSplitHasDisplay(
        program.weeklyScheduleSplit ?? program.weeklySplit,
      );
    case 'movement_slot_plan':
      return (
        (program.movementSlotPlan?.slots?.length ?? 0) > 0 ||
        exerciseAllocationsHaveDisplay(program.exerciseAllocations)
      );
    case 'exercise_selection':
      return (
        (program.exerciseSelections?.length ?? 0) > 0 ||
        generatedWorkoutsHaveDisplay(program.workouts)
      );
    case 'final_program_review':
      return !!program.finalProgram || generatedWorkoutsHaveDisplay(program.workouts);
    case 'schema_validation':
      return !!program.schemaValidationResult || !!program.validationReport;
    case 'finalising':
      return isProgramBuildComplete(program);
    default:
      return false;
  }
}

function inferFailureStepFromArtifacts(
  program: FitnessPlanProgram,
): ProgramGenerationStep | undefined {
  if (program.schemaValidationResult || program.validationReport) {
    return 'schema_validation';
  }
  if (program.finalProgram || program.workouts?.length) {
    if (program.weeklyScheduleSplit || program.weeklySplit) {
      return 'final_program_review';
    }
    return 'weekly_schedule_split';
  }
  if (program.exerciseSelections?.length) {
    return 'weekly_schedule_split';
  }
  if (program.movementSlotPlan?.slots?.length) {
    return 'exercise_selection';
  }
  if (program.weeklyScheduleSplit || program.weeklySplit) {
    return 'movement_slot_plan';
  }
  if (program.workoutBlueprints?.length) {
    return 'workout_blueprints';
  }
  if (program.phase) {
    return 'program_phase';
  }
  if (program.trainingStrategy) {
    return 'training_strategy';
  }
  if (program.goalProfile) {
    return 'goal_profile';
  }
  return undefined;
}

export function inferredFailureStep(
  program: FitnessPlanProgram,
): ProgramGenerationStep | undefined {
  const fromArtifacts = inferFailureStepFromArtifacts(program);
  const fromGen = normalizeGenerationStep(program.generation?.currentStep);
  if (!fromGen) {
    return fromArtifacts ?? 'goal_profile';
  }
  if (!isProgramBuildFailed(program)) {
    return fromGen;
  }
  if (
    fromGen === 'goal_profile' &&
    fromArtifacts &&
    fromArtifacts !== 'goal_profile'
  ) {
    return fromArtifacts;
  }
  if (
    fromArtifacts &&
    buildStepIndex(fromArtifacts) > buildStepIndex(fromGen)
  ) {
    return fromArtifacts;
  }
  return fromGen;
}

export function failureStepForProgram(
  program: FitnessPlanProgram,
): ProgramGenerationStep | undefined {
  const inferred = inferredFailureStep(program);
  const fromGen = normalizeGenerationStep(program.generation?.currentStep);
  if (!fromGen) {
    return inferred;
  }
  if (fromGen === 'goal_profile' && inferred !== 'goal_profile') {
    return inferred;
  }
  return fromGen;
}

export function buildFailureErrorMessage(
  program: FitnessPlanProgram,
  jobError?: string | null,
): string {
  const fromGen = program.generation?.error?.trim();
  if (fromGen) {
    return fromGen;
  }
  const fromMsg =
    program.generation?.status === 'failed'
      ? program.generation.message?.trim()
      : '';
  if (fromMsg && fromMsg !== 'Program generation failed.') {
    return fromMsg;
  }
  const fromProgram = program.generationError?.trim();
  if (fromProgram) {
    return fromProgram;
  }
  const fromJob = jobError?.trim();
  if (fromJob) {
    return fromJob;
  }
  return 'Program generation failed.';
}

export interface BuildFailureContext {
  failedStep: ProgramGenerationStep;
  failedStepLabel: string;
  progressPercent: number | null;
  updatedAtLabel: string | null;
  completedStepLabels: string[];
  errorLines: string[];
  retryHistory?: Record<string, unknown>[];
  failedArtifact?: string;
}

export function parseFailureErrorLines(message: string): string[] {
  const trimmed = message.trim();
  if (!trimmed) {
    return [];
  }
  const parts = trimmed
    .split(/;\s+/)
    .map((line) => line.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts : [trimmed];
}

function formatGenerationTimestamp(iso: string | undefined): string | null {
  if (!iso?.trim()) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
}

export function buildFailureContext(
  program: FitnessPlanProgram,
  jobError?: string | null,
): BuildFailureContext {
  const failedStep = failureStepForProgram(program) ?? 'goal_profile';
  const normalized = normalizeGenerationStep(failedStep) ?? failedStep;
  const errorMessage = buildFailureErrorMessage(program, jobError);
  const completedStepLabels = BUILD_STEP_ORDER.filter(
    (step) => buildStepIndex(step) < buildStepIndex(normalized),
  ).map((step) => BUILD_STEP_LABELS[step]);
  const schema = program.schemaValidationResult;
  const errorLines =
    schema?.errors?.length
      ? schema.errors
      : parseFailureErrorLines(errorMessage);
  return {
    failedStep: normalized,
    failedStepLabel: BUILD_STEP_LABELS[normalized],
    progressPercent: program.generation?.progressPercent ?? null,
    updatedAtLabel: formatGenerationTimestamp(program.generation?.updatedAt),
    completedStepLabels,
    errorLines,
    retryHistory: program.retryHistory,
    failedArtifact:
      typeof schema?.failedArtifact === 'string'
        ? schema.failedArtifact
        : undefined,
  };
}

export function buildFailureErrorClipboardText(
  program: FitnessPlanProgram,
  jobError?: string | null,
): string {
  const ctx = buildFailureContext(program, jobError);
  const lines = [
    `Failed during: ${ctx.failedStepLabel}`,
    ctx.failedArtifact ? `Failed artifact: ${ctx.failedArtifact}` : '',
    ctx.progressPercent !== null ? `Progress: ${ctx.progressPercent}%` : '',
    ctx.updatedAtLabel ? `When: ${ctx.updatedAtLabel}` : '',
    ctx.completedStepLabels.length
      ? `Completed: ${ctx.completedStepLabels.join(' → ')}`
      : '',
    '',
    ...ctx.errorLines,
  ];
  return lines.filter((line) => line.length > 0).join('\n');
}

export function buildStepUiState(
  step: ProgramGenerationStep,
  program: FitnessPlanProgram,
): BuildStepUiState {
  const normalizedStep = normalizeGenerationStep(step) ?? step;
  const current = isProgramBuildFailed(program)
    ? failureStepForProgram(program)
    : normalizeGenerationStep(program.generation?.currentStep) ??
      inferredFailureStep(program);
  const status = program.generation?.status;
  if (isProgramBuildFailed(program)) {
    if (current === normalizedStep) {
      return 'failed';
    }
    const failStep = failureStepForProgram(program);
    if (failStep && buildStepIndex(normalizedStep) < buildStepIndex(failStep)) {
      return 'complete';
    }
    if (buildStepReached(program, normalizedStep) && normalizedStep !== current) {
      return 'complete';
    }
    if (current && buildStepIndex(normalizedStep) < buildStepIndex(current)) {
      return 'complete';
    }
    return 'pending';
  }
  if (!current) {
    return 'pending';
  }
  const idx = buildStepIndex(normalizedStep);
  const cur = buildStepIndex(current);
  if (idx < cur) {
    return 'complete';
  }
  if (idx === cur && (status === 'queued' || status === 'running')) {
    return 'active';
  }
  if (idx === cur && status === 'completed') {
    return 'complete';
  }
  if (idx <= cur && status === 'completed') {
    return 'complete';
  }
  return 'pending';
}

export function artifactUnlocked(
  program: FitnessPlanProgram,
  threshold: ProgramGenerationStep,
): boolean {
  if (isProgramBuildComplete(program)) {
    return true;
  }
  return stepAtLeast(program.generation?.currentStep, threshold);
}

export function artifactUnlockedForDisplay(
  program: FitnessPlanProgram,
  producedAt: ProgramGenerationStep,
  unlockMode: ArtifactUnlockMode,
): boolean {
  if (isProgramBuildComplete(program)) {
    return true;
  }
  const current = normalizeGenerationStep(program.generation?.currentStep);
  if (!current) {
    return false;
  }
  const curIdx = buildStepIndex(current);
  const prodIdx = buildStepIndex(producedAt);
  if (unlockMode === 'duringOrAfter') {
    return curIdx >= prodIdx;
  }
  return curIdx > prodIdx;
}

export function artifactProducedBeforeFailure(
  program: FitnessPlanProgram,
  producedAt: ProgramGenerationStep,
  hasData: boolean,
): boolean {
  if (!hasData || !isProgramBuildFailed(program)) {
    return false;
  }
  const failStep = failureStepForProgram(program);
  if (!failStep) {
    return hasData;
  }
  const producedIdx = buildStepIndex(producedAt);
  const failIdx = buildStepIndex(failStep);
  if (producedIdx < failIdx) {
    return true;
  }
  if (producedAt === failStep && producedAt === 'schema_validation') {
    return !!program.schemaValidationResult || !!program.validationReport;
  }
  if (producedAt === failStep && producedAt === 'exercise_selection') {
    return (program.workouts?.length ?? 0) > 0;
  }
  if (producedAt === failStep && producedAt === 'movement_slot_plan') {
    return (program.movementSlotPlan?.slots?.length ?? 0) > 0;
  }
  return false;
}

export function artifactVisible(
  program: FitnessPlanProgram,
  producedAt: ProgramGenerationStep,
  hasDisplayableContent: boolean,
  unlockMode: ArtifactUnlockMode = 'afterProduced',
): boolean {
  if (!hasDisplayableContent) {
    return false;
  }
  if (isProgramBuildFailed(program)) {
    if (buildStepReached(program, producedAt)) {
      return true;
    }
    return artifactProducedBeforeFailure(
      program,
      producedAt,
      hasDisplayableContent,
    );
  }
  return artifactUnlockedForDisplay(program, producedAt, unlockMode);
}

export function isValidationRetryInProgress(
  program: FitnessPlanProgram,
): boolean {
  if (!isProgramBuildRunning(program)) {
    return false;
  }
  const msg = program.generation?.message?.trim() ?? '';
  if (msg.includes('Repairing') || msg.includes('Retrying')) {
    return true;
  }
  return (program.retryHistory?.length ?? 0) > 0;
}

export function schemaValidationArtifactVisible(
  program: FitnessPlanProgram,
  hasReport: boolean,
): boolean {
  if (!hasReport) {
    return false;
  }
  if (isProgramBuildComplete(program)) {
    return true;
  }
  if (isValidationRetryInProgress(program)) {
    return true;
  }
  if (isProgramBuildFailed(program)) {
    if (buildStepReached(program, 'schema_validation')) {
      return true;
    }
    return artifactProducedBeforeFailure(program, 'schema_validation', hasReport);
  }
  return artifactUnlockedForDisplay(program, 'schema_validation', 'duringOrAfter');
}

/** @deprecated use schemaValidationArtifactVisible */
export function validationArtifactVisible(
  program: FitnessPlanProgram,
  hasReport: boolean,
): boolean {
  return schemaValidationArtifactVisible(program, hasReport);
}

export function generatedWorkoutArtifactVisible(
  program: FitnessPlanProgram,
  hasDisplayableContent: boolean,
): boolean {
  if (!hasDisplayableContent) {
    return false;
  }
  if (isProgramBuildFailed(program)) {
    if (buildStepReached(program, 'exercise_selection')) {
      return true;
    }
    return artifactProducedBeforeFailure(
      program,
      'exercise_selection',
      hasDisplayableContent,
    );
  }
  if (isProgramBuildComplete(program)) {
    return true;
  }
  const current = normalizeGenerationStep(program.generation?.currentStep);
  const status = program.generation?.status;
  if (
    current === 'schema_validation' &&
    (status === 'queued' || status === 'running') &&
    !isValidationRetryInProgress(program)
  ) {
    return false;
  }
  if (
    current === 'movement_slot_plan' &&
    (status === 'queued' || status === 'running') &&
    !isValidationRetryInProgress(program)
  ) {
    return false;
  }
  return artifactUnlockedForDisplay(
    program,
    'exercise_selection',
    'duringOrAfter',
  );
}
